import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { CustomerSearchPicker } from "../../Components/CustomerSearchField";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import CustomToast from "../../Components/CustomToast";
import "../../Styles/pages/product.scss";
import "../../Styles/components/segment.scss";
import "../../Styles/components/tags.scss";

const AddAudienceMemberDialog = ({ visible, onHide, audienceId, onMemberAdded, existingMemberBcIds = [] }) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSubscriber, setSelectedSubscriber] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [resultsOpen, setResultsOpen] = useState(false);
    const [awaitingResults, setAwaitingResults] = useState(false);
    const [saveClicked, setSaveClicked] = useState(false);
    const [toastTrigger, setToastTrigger] = useState(false);
    const [toastData, setToastData] = useState({ summary: "", severity: "", detail: "", life: 0 });

    const searchPendingRef = useRef(false);

    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const {
        exactFilteredBusinessCustomers,
        fetchingExactFilteredBusinessCustomers,
        addingAudienceMember,
        addedAudienceMember,
        addAudienceMemberError,
    } = useSelector((state) => state.businessCustomers);

    const searchSubscribers = useCallback(
        (businessId, query) => dispatch(BusinessCustomersActions.exactFilteredBusinessCustomerRequest(businessId, query)),
        [dispatch]
    );
    const addMember = useCallback(
        (id, businessCustomerId) => dispatch(BusinessCustomersActions.addAudienceMemberRequest(id, businessCustomerId)),
        [dispatch]
    );

    useEffect(() => {
        if (!searchPendingRef.current || fetchingExactFilteredBusinessCustomers) return;
        searchPendingRef.current = false;
        setAwaitingResults(false);
        setSuggestions(exactFilteredBusinessCustomers || []);
    }, [exactFilteredBusinessCustomers, fetchingExactFilteredBusinessCustomers]);

    useEffect(() => {
        if (!addingAudienceMember && addedAudienceMember && saveClicked) {
            setToastData({
                summary: t("success"),
                severity: "success",
                detail: t("business_customers.audience_add_member_success"),
                life: 3000,
            });
            setToastTrigger(true);
        }
        if (!addingAudienceMember && addAudienceMemberError && saveClicked) {
            const errorDetail = addAudienceMemberError?.detail?.split('"')?.[1] ?? addAudienceMemberError?.detail;
            setToastData({
                summary: t("err"),
                severity: "error",
                detail: errorDetail || t("business_customers.audience_add_member_error"),
                life: 4000,
            });
            setToastTrigger(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addingAudienceMember, addedAudienceMember, addAudienceMemberError, saveClicked]);

    const resetForm = () => {
        searchPendingRef.current = false;
        setSearchQuery("");
        setSelectedSubscriber(null);
        setSuggestions([]);
        setResultsOpen(false);
        setAwaitingResults(false);
        setSaveClicked(false);
    };

    const handleHide = () => {
        resetForm();
        onHide();
    };

    const handleSearch = () => {
        const query = searchQuery.trim();
        if (query.length < 3 || !defMembership?.business?.id) return;
        searchPendingRef.current = true;
        setAwaitingResults(true);
        setSuggestions([]);
        setResultsOpen(true);
        searchSubscribers(defMembership.business.id, query);
    };

    const handleAdd = () => {
        if (!selectedSubscriber?.id || !audienceId) {
            return;
        }
        if (existingMemberBcIds.includes(selectedSubscriber.id)) {
            setToastData({
                summary: t("err"),
                severity: "error",
                detail: t("business_customers.audience_add_member_error"),
                life: 4000,
            });
            setToastTrigger(true);
            return;
        }
        setSaveClicked(true);
        addMember(audienceId, selectedSubscriber.id);
    };

    return (
        <Dialog
            header={t("business_customers.audience_add_member_header")}
            visible={visible}
            style={{ width: "400px" }}
            onHide={handleHide}
        >
            <CustomToast
                {...toastData}
                trigger={toastTrigger}
                handleActionsAfterToast={() => {
                    setToastTrigger(false);
                    setSaveClicked(false);
                    handleHide();
                    onMemberAdded?.();
                }}
                handleActionsAfterErrorToast={() => {
                    setToastTrigger(false);
                    setSaveClicked(false);
                }}
            />

            <div className="p-fluid add-main-div">
                <p className="normal_hint mb-3">{t("business_customers.audience_add_member_hint")}</p>

                <div className="mb-1">
                    <label htmlFor="audience_member_search" className="tag_label">
                        {t("business_customers.audience_choose_subscriber")}
                    </label>
                </div>

                <CustomerSearchPicker
                    id="audience_member_search"
                    className="w-full"
                    value={searchQuery}
                    suggestions={suggestions}
                    open={resultsOpen}
                    loading={awaitingResults && resultsOpen}
                    emptyLabel={t("common.no_data")}
                    placeholder={t("common.global_search")}
                    onChange={(value) => {
                        searchPendingRef.current = false;
                        setAwaitingResults(false);
                        setSelectedSubscriber(null);
                        setSearchQuery(value);
                        setResultsOpen(false);
                    }}
                    onSearch={handleSearch}
                    onClear={() => {
                        searchPendingRef.current = false;
                        setAwaitingResults(false);
                        setSearchQuery("");
                        setSelectedSubscriber(null);
                        setSuggestions([]);
                        setResultsOpen(false);
                    }}
                    onClose={() => {
                        searchPendingRef.current = false;
                        setAwaitingResults(false);
                        setResultsOpen(false);
                    }}
                    onSelect={(customer) => {
                        setSelectedSubscriber(customer);
                        setSearchQuery(customer?.name || "");
                        setResultsOpen(false);
                    }}
                />

                <div className="p-field p-mb-3 mt-4">
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2rem" }}>
                        <Button
                            label={t("subscriber.cancel")}
                            className="mr-1"
                            icon="pi pi-times"
                            onClick={handleHide}
                            id="cancel-tag-button"
                            type="button"
                        />
                        <Button
                            type="button"
                            label={t("business_customers.audience_add_member")}
                            className="p-mt-2 save-btns"
                            onClick={handleAdd}
                            disabled={!selectedSubscriber?.id || addingAudienceMember || saveClicked}
                            loading={addingAudienceMember}
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default AddAudienceMemberDialog;
